'use client'
import { useState, useEffect } from 'react'
import { useCurrency } from '@/lib/currency'
import { useAuth } from '@/context/AuthContext'
import Link from 'next/link'
import Image from 'next/image'
import { nameToSlug } from '@/lib/slugs'

const SORT_OPTIONS = [
  { key: 'deals', label: 'Most deals' },
  { key: 'price', label: 'Lowest price' },
  { key: 'discount', label: 'Most below avg' },
  { key: 'recent', label: 'Recently detected' },
]

function sortList(list, key) {
  return [...list].sort((a, b) => {
    if (key === 'price') {
      const pa = a.minPrice ?? Infinity
      const pb = b.minPrice ?? Infinity
      return pa - pb || b.dealCount - a.dealCount
    }
    if (key === 'recent') {
      return [...list].sort((a, b) =>
        (b.latestFetched ?? '').localeCompare(a.latestFetched ?? '')
      )
    }
    if (key === 'discount') {
      const da = a.maxDiscount ?? -1
      const db = b.maxDiscount ?? -1
      return db - da || b.dealCount - a.dealCount
    }
    return b.dealCount - a.dealCount || a.name.localeCompare(b.name)
  })
}

function hoursAgo(iso) {
  if (!iso) return null
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000)
  if (h < 1) return 'just now'
  if (h < 48) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function AirlineCard({ a, priority = false }) {
  const { formatDealPrice } = useCurrency()
  return (
    <Link
      href={`/airlines/${nameToSlug(a.name)}`}
      className="bg-white rounded-xl border border-gray-200 p-4 hover:border-blue-400 hover:shadow-sm transition-all flex flex-col gap-2 group"
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <Image src={a.logo} alt="" width={120} height={32} className="h-8 w-auto object-contain self-start" unoptimized priority={priority} />
      <span className="font-semibold text-gray-900 text-sm leading-tight group-hover:text-blue-700 transition-colors">
        {a.name}
      </span>
      <span className="text-xs text-gray-500">Hub: {a.hub}</span>
      {a.dealCount > 0 ? (
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-bold text-blue-600">
            {a.dealCount} deal{a.dealCount !== 1 ? 's' : ''}
          </span>
          <div className="flex gap-1.5 text-xs">
            {a.minPrice != null && <span className="text-gray-400">from {formatDealPrice(a.minPrice).primary}</span>}
            {(a.maxDiscount ?? 0) >= 15 && <span className="text-green-600">up to -{a.maxDiscount}%</span>}
          </div>
          {hoursAgo(a.latestFetched) && (
            <span className="text-xs text-gray-400">{hoursAgo(a.latestFetched)}</span>
          )}
        </div>
      ) : (
        <span className="text-xs text-gray-400">No deals now</span>
      )}
    </Link>
  )
}

export default function AirlinesSortedList({ budget, fullService }) {
  const [sort, setSort] = useState('deals')
  const [budgetFirst, setBudgetFirst] = useState(false)
  const [isPaid, setIsPaid] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const { currentUser, accessToken } = useAuth()

  useEffect(() => {
    if (!accessToken) { setIsPaid(false); return }
    fetch('/api/profile', { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => setIsPaid(d?.tier === 'plus' || d?.tier === 'pro'))
      .catch(() => {})
  }, [accessToken])

  const sections = budgetFirst
    ? [{ label: 'Budget carriers', list: budget }, { label: 'Full-service carriers', list: fullService }]
    : [{ label: 'Full-service carriers', list: fullService }, { label: 'Budget carriers', list: budget }]

  return (
    <>
      {isPaid ? (
        <>
          <button
            onClick={() => setFiltersOpen(v => !v)}
            className="mb-4 flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg className={`w-3.5 h-3.5 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
            {filtersOpen ? 'Hide filters' : 'Sort'}
          </button>
          {filtersOpen && (
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <span className="text-xs text-gray-400 mr-1">Sort by</span>
              {SORT_OPTIONS.map((o) => (
                <button
                  key={o.key}
                  onClick={() => setSort(o.key)}
                  className={`text-xs px-3 py-1.5 rounded-full font-medium border transition-colors ${
                    sort === o.key
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white border-gray-200 text-gray-500 hover:border-blue-400'
                  }`}
                >
                  {o.label}
                </button>
              ))}
              <button
                onClick={() => setBudgetFirst((v) => !v)}
                className="ml-auto text-xs px-3 py-1.5 rounded-full font-medium bg-white border border-gray-200 text-gray-500 hover:border-blue-400 transition-colors"
              >
                {budgetFirst ? 'Full-service first' : 'Budget first'}
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="mb-6 flex items-center gap-2 text-sm text-gray-400">
          <span className="text-base">🔒</span>
          <span>Sort —{' '}
            <a href="/pricing" className="text-blue-500 hover:underline font-medium">upgrade to Plus</a>
          </span>
        </div>
      )}

      {sections.map(({ label, list }) => (
        <section key={label} className="mb-10">
          <h2 className="text-xl font-semibold text-gray-700 mb-4">{label}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {sortList(list, sort).map((a, i) => (
              <AirlineCard key={a.code} a={a} priority={i < 4} />
            ))}
          </div>
        </section>
      ))}
    </>
  )
}
